import React, { useState, useEffect, useMemo } from 'react';

import { emailReg } from '@cryptoboyio/mercuryo-lab/utils';
import { useTranslation, Trans } from 'react-i18next';
import { useMatch } from 'react-router-dom';

import { bemCssModules, getTestId, handleCookie } from '@utils';
import { useNavigate } from '@utils/hooks';

import { useStore, loginActions, globalActions } from '@modules';
import { LoginParams } from '@modules/login/types';

import Button from '@components/Button';
import Captcha from '@components/Captcha';
import Illustration from '@components/Illustration';
import Switch from '@components/Switch';
import { useLoginContext } from '@containers/App/routes/login/LoginContext';
import LoginLayout from '@layouts/LoginLayout';

import Email from './Email';
import CloudsAndCash from './clouds_and_cash.svg';

import style from './Authorization.scss';

const cn = bemCssModules(style, 'Authorization');
const ti = getTestId('Authorization');
const { getCookie } = handleCookie;

export const Authorization: CustomFC = ({ className }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [accept, setAccept] = useState(false);
  const [{ login, user, global }, dispatch] = useStore();
  const { errors, loaders } = login;
  const { isShowCaptcha, captcha } = global;
  const validationErrors = errors?.data;

  const [{ email }] = useLoginContext();

  const isBlockedAccount = useMatch(CONST.nav.accountBlocked);

  useEffect(() => {
    return () => {
      dispatch(loginActions.clearErrors());
    };
  }, []);

  const handleChangeAgree = () => {
    setAccept(!accept);
  };

  const isLoading = !!loaders.login;
  const isSubmitDisabled =
    !emailReg.test(email) || !accept || isLoading || (isShowCaptcha && !captcha.length);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!isSubmitDisabled) {
      if (isShowCaptcha) dispatch(globalActions.setShowCaptcha(false));

      const referralCode = user.referralCode || getCookie('referral_code');
      const loginData: LoginParams = { accept, captcha };

      if (referralCode) loginData.referral_code = referralCode;

      loginData.email = email;

      dispatch(loginActions.login(loginData))
        .unwrap()
        .then(res => {
          if (res.data.next) navigate(`../${res.data.next}`);
        })
        .catch(err => err);
    }
  };

  const image = useMemo(
    () => (
      <Illustration images={['/img/rabbit.png', <CloudsAndCash />]} data-testid={ti('image')} />
    ),
    []
  );

  return (
    <LoginLayout image={image} backButton={false}>
      <form className={cn(null, [className])} onSubmit={handleSubmit} data-testid={ti()}>
        {!isBlockedAccount && <h1 className={cn('title')}>{t('public.login_title')}</h1>}
        <p className={cn('sub-title')}>{t(`public.sign_in_title.email`)}</p>

        <Email isLoading={isLoading} email={email} />

        <div className={cn('accept')} data-testid={ti('accept')}>
          <Switch className={cn('checkbox')} value={accept} onChange={handleChangeAgree}>
            <Trans i18nKey="public.agree_tos">
              I agree with the
              <a
                className={cn(['link'])}
                href={CONST.urls.termsUrl}
                target="_blank"
                rel="noreferrer"
              >
                Terms of Service
              </a>
            </Trans>
          </Switch>
          {validationErrors?.accept?.length && (
            <span className={cn('error-message')}>{validationErrors.accept.join('\n')}</span>
          )}
        </div>

        {isShowCaptcha && <Captcha className={cn('captcha')} />}

        <Button
          className={cn('button')}
          type="submit"
          disabled={isSubmitDisabled}
          data-testid={ti('submit')}
        >
          {t('public.get_the_code')}
        </Button>
      </form>
    </LoginLayout>
  );
};

export default Authorization;
